import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { fetchCart } = useCart();
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentInfo, setPaymentInfo] = useState(null);

  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentInfo(data);
        // Refresh cart to reflect cleared items
        await fetchCart();
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts >= maxAttempts - 1) {
        setStatus('error');
      } else {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    }
  }, [sessionId, fetchCart]);

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    pollPaymentStatus();
  }, [sessionId, isAuthenticated, navigate, pollPaymentStatus]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-[#D93052] mx-auto mb-6 animate-spin" />
          <h1 className="text-2xl font-bold text-[#1A3C34] font-['Chivo'] mb-2">
            Processing your payment...
          </h1>
          <p className="text-[#6B7280]">Please wait while we confirm your order.</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-3xl p-12 shadow-sm">
            <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-[#1A3C34] font-['Chivo'] mb-4">
              Payment Issue
            </h1>
            <p className="text-[#6B7280] mb-8">
              We couldn't verify your payment. Please check your email for confirmation or contact support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/orders">
                <Button 
                  className="rounded-full bg-[#D93052] text-white font-bold px-8 py-4 hover:bg-[#B02040]"
                >
                  View Orders
                </Button>
              </Link>
              <Link to="/products">
                <Button 
                  variant="outline"
                  className="rounded-full border-2 border-[#1A3C34] text-[#1A3C34] px-8 py-4"
                >
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24">
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="bg-white rounded-3xl p-12 shadow-sm">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          
          <h1 className="text-3xl font-bold text-[#1A3C34] font-['Chivo'] mb-4">
            Order Confirmed!
          </h1>
          
          <p className="text-[#6B7280] mb-2">
            Thank you for your order. Your gear is on its way!
          </p>
          
          {paymentInfo && (
            <p className="text-lg font-bold text-[#1A3C34] mb-8">
              Total: ${(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
            </p>
          )}

          <div className="bg-[#E8EFE9] rounded-2xl p-6 mb-8">
            <Package className="w-8 h-8 text-[#1A3C34] mx-auto mb-3" />
            <p className="text-sm text-[#6B7280]">
              You'll receive an email confirmation shortly with your order details.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/orders">
              <Button 
                className="rounded-full bg-[#D93052] text-white font-bold px-8 py-4 hover:bg-[#B02040]"
                data-testid="view-orders-btn"
              >
                View Orders
              </Button>
            </Link>
            <Link to="/products">
              <Button 
                variant="outline"
                className="rounded-full border-2 border-[#1A3C34] text-[#1A3C34] px-8 py-4 hover:bg-[#1A3C34] hover:text-white"
              >
                Continue Shopping
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
